import { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import { DataScienceEnums } from 'dashboard-services/api/dataScience'
import Query from 'dashboard-services/api/Query'
import { ReduxService } from 'dashboard-services/services'

import getApiConfig from 'api/ApiConfig'
import { tasksQueries } from 'queries/modules/datascience'

export default (() => {
  const EMPTY_ARRAY = []
  const EMPTY_OBJ = {}

  const getTaskParams = task => Object.fromEntries((task?.params || []).filter(p => p.type !== DataScienceEnums.TASK_PARAM_TYPE.OUTPUT).map(param => [param.name, param.defaultValue]))

  const useTaskInput = ({ setDynamicValidation }) => {
    const onChangeTaskInput = useCallback((value, { data }) =>
            setDynamicValidation(validation =>
              ReduxService.updateObject(
                validation, {
                  taskInput: ReduxService.updateObject(
                    validation.taskInput,
                    {
                      taskName: value,
                      taskGroupName: data?.task?.groupName,
                      taskVersion: data?.task?.version,
                      params: getTaskParams(data?.task)
                    }
                  )
                })
              )
            , [setDynamicValidation]),
          onChangeTaskVersion = useCallback((value, { data }) =>
            setDynamicValidation(validation =>
              ReduxService.updateObject(
                validation, {
                  taskInput: ReduxService.updateObject(
                    validation.taskInput,
                    {
                      taskVersion: value,
                      params: getTaskParams(data?.task)
                    }
                  )
                })
            )
          , [setDynamicValidation]),
          onChangeParamValue = useCallback((value, name) =>
            setDynamicValidation(validation =>
              ReduxService.updateObject(
                validation, {
                  taskInput: ReduxService.updateObject(
                    validation.taskInput,
                    {
                      params: ReduxService.updateObject(
                        validation.taskInput.params,
                        { [name]: value }
                      )
                    }
                  )
                })
            )
          , [setDynamicValidation])
    return useMemo(() => ({ onChangeTaskInput, onChangeTaskVersion, onChangeParamValue }), [onChangeTaskInput, onChangeTaskVersion, onChangeParamValue])
  }

  const useQueryTasks = ({ getApiConfig, input, ...rest }) => {
    const select = useCallback(data => data?.pages?.flatMap(page => page?.items).map(task => ({ label: `${task.readableName || task.name} (${task.groupName})`, value: task.name, task, tooltip: task.groupName })), []),
          getQueryBuilder = useCallback(() =>
            new Query.Builder(getApiConfig())
              .withFetching(false)
          , [getApiConfig])

    return tasksQueries.useSearchTasks({
      getQueryBuilder,
      query: input,
      onSelect: select,
      shouldRefetch: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      ...rest
    })
  }

  const useGetTaskVersion = ({ getApiConfig, taskGroupName, taskName, ...rest }) => {
    const select = useCallback(data => data?.pages?.flatMap(page => page?.items).find(t => t.groupName === taskGroupName && t.name === taskName)?.id, [taskGroupName, taskName]),
          getQueryBuilder = useCallback(() =>
            new Query.Builder(getApiConfig())
          , [getApiConfig])

    return tasksQueries.useSearchTasks({
      getQueryBuilder,
      query: `groupName=${taskGroupName}&${taskName}`,
      onSelect: select,
      shouldRefetch: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      size: 50,
      ...rest
    })
  }

  const useValidatorTask = ({ dynamicValidation, dynamicValidationFileContent, isEnabled }) => {
    const dispatch = useDispatch(),
          { taskInput = EMPTY_OBJ } = dynamicValidation || EMPTY_OBJ,
          { taskName, taskVersion, taskGroupName, params } = taskInput,
          getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
          [taskQuery, setTaskQuery] = useState(""),
          { fetchNextPage: fetchMoreTasks, isFetching: isFetchingTasks, isFetchingNextPage: isFetchingNextPageTasks, data: tasks = EMPTY_ARRAY } = useQueryTasks({
            input: `*${taskQuery}*`,
            getApiConfig: getApiConfigLocal,
            enabled: isEnabled
          }),
          defaultTask = useMemo(() => tasks.find(t => t.value === taskName) || (taskName ? ({ label: `${taskName} (${taskGroupName})`, value: taskName }) : undefined), [tasks, taskName, taskGroupName]),
          getQueryBuilder = useCallback(() =>
            new Query.Builder(getApiConfigLocal())
              .withFetching(false)
          , [getApiConfigLocal]),
          [versionsQuery, setVersionsQuery] = useState(),
          taskId = useGetTaskVersion({ getApiConfig: getApiConfigLocal, taskGroupName, taskName, enabled: isEnabled }).data,
          { data: versionsFromApi, fetchNextPage: fetchMoreVersions, isFetching: isFetchingVersions, isFetchingNextPage: isFetchingNextPageVersions } = tasksQueries.useGetTaskVersions({ getQueryBuilder, taskId, query: versionsQuery, size: 100, enabled: isEnabled }),
          versions = useMemo(() => (versionsFromApi?.pages || []).flatMap(p => p?.items || []).map(i => ({ value: i?.version, task: ReduxService.updateObject(i, { params: (i?.params || []).map(p => ReduxService.updateObject(p, { value: p?.defaultValue || "" })) }), label: "Version " + i?.version })), [versionsFromApi]),
          defaultTaskVersion = useMemo(() => versions.find(v => v.value === taskVersion) || (taskVersion ? ({ label: "Version " + taskVersion, value: taskVersion }) : undefined), [versions, taskVersion]),
          getAllParams = useCallback(() => params, [params]),
          taskParamNamesFromTaskVersion = useMemo(() => defaultTaskVersion?.task?.params?.map(p => p.name).toUnique(), [defaultTaskVersion?.task?.params]),
          paramsFromDynamicValidationFile = useMemo(() => Object.keys(dynamicValidationFileContent?.taskInput?.params || {}), [dynamicValidationFileContent?.taskInput?.params]),
          paramsCleanEntries = useMemo(() => Object.entries(params || {}).filter(([name]) => taskParamNamesFromTaskVersion?.includes(name)), [params, taskParamNamesFromTaskVersion]),
          invalidParamNames = useMemo(() => Object.keys(params || {}).filter(name => !taskParamNamesFromTaskVersion?.includes(name)), [params, taskParamNamesFromTaskVersion]),
          areAllInvalidParamsFromDVFile = invalidParamNames.map(p => paramsFromDynamicValidationFile.includes(p)).every(v => v === true)

    return useMemo(() => ({
      taskName,
      taskVersion,
      tasks,
      defaultTask,
      fetchMoreTasks,
      isFetchingTasks: isFetchingTasks || isFetchingNextPageTasks,
      setTaskQuery,
      versions,
      defaultTaskVersion,
      fetchMoreVersions,
      isFetchingVersions: isFetchingVersions || isFetchingNextPageVersions,
      setVersionsQuery,
      getAllParams,
      paramsCleanEntries,
      invalidParamNames,
      areAllInvalidParamsFromDVFile
    }), [areAllInvalidParamsFromDVFile, defaultTask, defaultTaskVersion, fetchMoreTasks, fetchMoreVersions, getAllParams, invalidParamNames, isFetchingNextPageTasks, isFetchingNextPageVersions, isFetchingTasks, isFetchingVersions, paramsCleanEntries, taskName, taskVersion, tasks, versions])
  }

  return {
    useTaskInput,
    useValidatorTask
  }
})()
